import React, { useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  Avatar,
  Tooltip
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';
import {
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  Radar as RadarChartIcon,
  Instagram,
  Twitter,
  Facebook,
  LinkedIn
} from '@mui/icons-material';
import { getPlatformIcon, getPlatformColor } from '../../utils/platformUtils';

// Platform color scheme
const PLATFORM_COLORS = {
  instagram: '#E4405F',
  twitter: '#1DA1F2',
  facebook: '#1877f2',
  linkedin: '#0077b5',
  tiktok: '#000000',
  youtube: '#FF0000',
  pinterest: '#BD081C',
  snapchat: '#FFFC00'
};

/**
 * Custom tooltip for platform charts
 */
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    
    return (
      <Box
        sx={{
          bgcolor: 'background.paper',
          p: 2,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          boxShadow: 2,
          minWidth: 200
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Avatar
            sx={{
              bgcolor: PLATFORM_COLORS[data.platform] || '#gray',
              width: 24,
              height: 24
            }}
          >
            {React.createElement(getPlatformIcon(data.platform) || Twitter)}
          </Avatar>
          <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>
            {data.platform}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Posts:</Typography>
            <Typography variant="body2" fontWeight="medium">
              {data.posts?.toLocaleString()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Impressions:</Typography>
            <Typography variant="body2" fontWeight="medium">
              {data.impressions?.toLocaleString()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Engagement:</Typography>
            <Typography variant="body2" fontWeight="medium">
              {data.engagement?.toLocaleString()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Engagement Rate:</Typography>
            <Typography variant="body2" fontWeight="medium">
              {data.engagementRate?.toFixed(1)}%
            </Typography>
          </Box>
          {data.revenue && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Revenue:</Typography>
              <Typography variant="body2" fontWeight="medium">
                ${data.revenue.toLocaleString()}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  }
  return null;
};

/**
 * Custom label for pie chart segments
 */
const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, platform }) => {
  if (percent < 0.05) return null; // Don't show labels for segments < 5%
  
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      fontSize="12"
      fontWeight="bold"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

/**
 * Platform Comparison Chart Component
 * 
 * Displays platform performance comparison with multiple visualization options
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Platform comparison data
 * @param {boolean} props.loading - Loading state
 * @param {string} props.metric - Metric to compare ('engagement', 'impressions', 'revenue')
 * @param {string} props.chartType - Chart type ('pie', 'bar', 'radar')
 * @param {number} props.height - Chart height
 */
const PlatformComparisonChart = ({
  data = [],
  loading = false,
  metric: initialMetric = 'engagement',
  chartType: initialChartType = 'pie',
  height = 300
}) => {
  const theme = useTheme();
  const [chartType, setChartType] = useState(initialChartType);
  const [metric, setMetric] = useState(initialMetric);

  // Process data for charts
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    return data
      .filter(item => item[metric] > 0)
      .sort((a, b) => b[metric] - a[metric])
      .map(item => ({
        ...item,
        fill: PLATFORM_COLORS[item.platform] || theme.palette.primary.main,
        displayName: item.platform.charAt(0).toUpperCase() + item.platform.slice(1)
      }));
  }, [data, metric, theme]);

  // Calculate totals and percentages
  const totals = useMemo(() => {
    const total = chartData.reduce((sum, item) => sum + item[metric], 0);
    return {
      total,
      platforms: chartData.length,
      average: total / chartData.length || 0
    };
  }, [chartData, metric]);

  /**
   * Handle chart type change
   */
  const handleChartTypeChange = (event, newType) => {
    if (newType !== null) {
      setChartType(newType);
    }
  };

  /**
   * Handle metric change
   */
  const handleMetricChange = (event, newMetric) => {
    if (newMetric !== null) {
      setMetric(newMetric);
    }
  };

  /**
   * Render chart based on selected type
   */
  const renderChart = () => {
    switch (chartType) {
      case 'bar':
        return (
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis 
              dataKey="displayName"
              stroke={theme.palette.text.secondary}
              fontSize={12}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis 
              stroke={theme.palette.text.secondary}
              fontSize={12}
            />
            <RechartsTooltip content={<CustomTooltip />} />
            <Bar 
              dataKey={metric}
              fill={theme.palette.primary.main}
              radius={[4, 4, 0, 0]}
            >
              {chartData.map((entry) => (
                <Cell key={entry.platform} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        );

      case 'radar':
        // Normalize data for radar chart (0-100 scale)
        const maxValue = Math.max(...chartData.map(item => item[metric]));
        const radarData = chartData.map(item => ({
          ...item,
          [metric]: (item[metric] / maxValue) * 100
        }));

        return (
          <RadarChart data={radarData} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
            <PolarGrid />
            <PolarAngleAxis 
              dataKey="displayName" 
              tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
            />
            <PolarRadiusAxis 
              angle={90} 
              domain={[0, 100]}
              tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
            />
            <Radar
              name={metric.charAt(0).toUpperCase() + metric.slice(1)}
              dataKey={metric}
              stroke={theme.palette.primary.main}
              fill={theme.palette.primary.main}
              fillOpacity={0.3}
              strokeWidth={2}
            />
            <RechartsTooltip content={<CustomTooltip />} />
          </RadarChart>
        );

      default: // pie chart
        return (
          <PieChart margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderPieLabel}
              outerRadius={Math.min(height * 0.35, 100)}
              fill="#8884d8"
              dataKey={metric}
            >
              {chartData.map((entry) => (
                <Cell key={entry.platform} fill={entry.fill} />
              ))}
            </Pie>
            <RechartsTooltip content={<CustomTooltip />} />
            <Legend 
              verticalAlign="bottom" 
              height={36}
              formatter={(value, entry) => (
                <span style={{ color: entry.color }}>
                  {entry.payload.displayName}
                </span>
              )}
            />
          </PieChart>
        );
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height,
        textAlign: 'center' 
      }}>
        <PieChartIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          No platform data available
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Connect social media platforms to see performance comparison
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Chart Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        {/* Chart Type Selector */}
        <ToggleButtonGroup
          value={chartType}
          exclusive
          onChange={handleChartTypeChange}
          size="small"
        >
          <ToggleButton value="pie">
            <Tooltip title="Pie Chart">
              <PieChartIcon />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="bar">
            <Tooltip title="Bar Chart">
              <BarChartIcon />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="radar">
            <Tooltip title="Radar Chart">
              <RadarChartIcon />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Metric Selector */}
        <ToggleButtonGroup
          value={metric}
          exclusive
          onChange={handleMetricChange}
          size="small"
        >
          <ToggleButton value="engagement">
            Engagement
          </ToggleButton>
          <ToggleButton value="impressions">
            Impressions
          </ToggleButton>
          <ToggleButton value="revenue">
            Revenue
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Chart Container */}
      <ResponsiveContainer width="100%" height={height}>
        {renderChart()}
      </ResponsiveContainer>

      {/* Summary Stats */}
      <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Platform Summary:
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="primary">
              {totals.platforms}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Platforms
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="primary">
              {typeof totals.total === 'number' ? totals.total.toLocaleString() : totals.total}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
              Total {metric}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="primary">
              {typeof totals.average === 'number' ? Math.round(totals.average).toLocaleString() : totals.average}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
              Average {metric}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PlatformComparisonChart;